<template>
  <input
    v-if="type !== 'textarea'"
    v-bind="sharedProps"
    :id="id"
    ref="input"
    v-model="model"
    :type="type === 'alphanumeric' ? 'text' : type"
    :class="[
      'input text-base initial:w-full',
      {
        'input-primary bg-base-200 text-base-content': theme === 'default',
        'input-lg': size === 'lg',
        'input-md': size === 'md',
        'input-sm': size === 'sm',
        'input-xs': size === 'xs',
      },
    ]"
    @input="validateInput"
    @blur="handleBlur"
  />
  <textarea
    v-else
    v-bind="sharedProps"
    :id="id"
    ref="input"
    v-model="model"
    :class="[
      'textarea w-full text-base',
      {
        'textarea-primary border bg-base-200 text-base-content': theme === 'default',
        'resize-none': !resize,
      },
    ]"
    @input="validateInput"
    @blur="handleBlur"
  ></textarea>
</template>
<script lang="ts" setup>
  import { computed, ref } from 'vue';
  import { useVModel } from '@vueuse/core';
  import { generateId } from '@core/utils';
  import { Maybe } from '@shared/utils';

  export interface InputValidationSchema {
    pattern: string;
    title: string;
  }

  export type InputValueType = Maybe<string | number>;
  export type InputType = 'text' | 'textarea' | 'password' | 'number' | 'tel' | 'email' | 'alphanumeric';
  export interface MInputProps {
    type?: InputType;
    theme?: 'default' | 'custom';
    name?: string | null;
    id?: string;
    modelValue?: InputValueType;
    readonly?: boolean;
    resize?: boolean;
    size?: 'xs' | 'sm' | 'md' | 'lg';
  }

  export interface Emits {
    (e: 'update:modelValue', modelValue: InputValueType): void;
    (e: 'blur'): void;
  }
  const emit = defineEmits<Emits>();

  const props = withDefaults(defineProps<MInputProps>(), {
    type: 'text',
    theme: 'default',
    name: null,
    id: generateId(),
    placeholder: null,
    modelValue: '',
    resize: false,
    size: 'md',
  });

  const sharedProps = computed(() => {
    const { id, readonly } = props;
    return {
      id,
      readonly,
      name: nameProp.value,
    };
  });

  const input = ref<HTMLInputElement>();
  const nameProp = computed<string>(() => props.name || props.id);
  const model = useVModel(props, 'modelValue', emit);
  defineExpose({
    focus: () => input.value && (input.value as HTMLInputElement).focus(),
    valid: (): boolean => !!input.value && (input.value as HTMLInputElement).validity.valid,
    id: props.id,
  });

  const handleBlur = () => {
    validateInput();
    emit('blur');
  };

  const validateInput = () => {
    // First handle specific alphanumeric validation
    if (props.type === 'alphanumeric') {
      const alphanumericRegex = /^[a-zA-Z0-9\s]+$/;
      if (!alphanumericRegex.test(model.value as string)) {
        model.value = (model.value as string).replace(/[^a-zA-Z0-9\s]/g, '');
      }
      return;
    }

    // For all input types, remove tildes
    if (typeof model.value === 'string' && model.value.includes('~')) {
      model.value = model.value.replace(/~/g, '');
    }
  };
</script>

<style scoped lang="css">
  /*
    Using tailwind text-base to override daisyui .input and .textarea font-size
    16px prevents ios auto zooming
    https://css-tricks.com/16px-or-larger-text-prevents-ios-form-zoom/

    error style set in MFormControl.vue
  */

  /* start default or on white background */
  input,
  textarea,
  .bg-white input,
  .bg-white textarea {
    @apply border border-base-300 bg-base-100;
  }

  input[disabled],
  textarea[disabled],
  .bg-white input[disabled],
  .bg-white textarea[disabled] {
    @apply bg-base-300 text-opacity-50 placeholder-opacity-50;
  }
  /* end default or on white background */

  /* start on base-100 */
  .bg-base-100 input,
  .bg-base-100 textarea {
    --tw-bg-opacity: 1;
    background-color: rgb(255 255 255 / var(--tw-bg-opacity));
  }

  .bg-base-100 input[disabled],
  .bg-base-100 textarea[disabled] {
    @apply bg-base-300 text-opacity-50 placeholder-opacity-50;
  }
  /* end on base-100 */

  /* start on base-200 */
  /* end on base-200 */

  /* start on base-300 */
  .bg-base-300 input,
  .bg-base-300 textarea {
    --tw-bg-opacity: 1;
    background-color: rgb(255 255 255 / var(--tw-bg-opacity));
  }

  .bg-base-300 input[disabled],
  .bg-base-300 textarea[disabled] {
    @apply bg-base-200 text-opacity-50 placeholder-opacity-50;
  }
  /* end on base-300 */

  /* start on accent */
  .bg-accent input,
  .bg-accent textarea {
    @apply bg-base-300 text-primary;
  }

  .bg-accent input[disabled],
  .bg-accent textarea[disabled] {
    @apply bg-opacity-50 text-opacity-50 placeholder-opacity-50;
  }
  /* end on accent */
</style>
